import { UseQueryOptions } from '@tanstack/react-query';
import {
	ItemPage,
	ProjectSpaceConfig,
	getAllByIdsPath,
	getPagePath,
	savePath,
	scopedClientKey,
	scopedProjectKey,
} from 'ihub/common';
import { CRUDHelperBase } from '../classes/crud-helper-base';
import {
	buildURL,
	getTenantID,
	handleResponse,
	standardPost,
	validateOkResponse,
} from '../classes/http-helper-const';
import QueryParameter from '../classes/query-parameter';
import { cacheStaleTime } from '../constants';
import HttpResponseOptions from '../interface/http-response-options';

export class ProjectSpaceHttpService extends CRUDHelperBase<ProjectSpaceConfig> {
	constructor() {
		super(ProjectSpaceConfig.name);
	}
	override getByID(
		Id: string,
		queryParams?: QueryParameter[],
		additionalHeaders?: HeadersInit
	): UseQueryOptions<ProjectSpaceConfig, Error, ProjectSpaceConfig, readonly unknown[]> {
		return super.getByID(Id, queryParams, { [scopedProjectKey]: Id });
	}

	override delete(
		itemId: string,
		options?: HttpResponseOptions,
		queryParams?: QueryParameter[],
		additionalHeaders?: HeadersInit,
		remove?: boolean
	): Promise<void> {
		return super.delete(itemId, options, queryParams, { [scopedProjectKey]: itemId }, remove);
	}

	override async save(
		item: ProjectSpaceConfig,
		options?: HttpResponseOptions,
		queryParams?: QueryParameter[],
		additionalHeaders?: HeadersInit,
		invalidateKeys?: boolean
	): Promise<void> {
		const url = buildURL(this.objectType, [savePath], queryParams);
		const response = await standardPost(url, item, { [scopedProjectKey]: item.id });
		await handleResponse(response, 'save', this.objectType, options);
		if (invalidateKeys) {
			await this.invalidateKeys();
		}
	}

	async create(
		item: ProjectSpaceConfig,
		clientKey: string,
		options?: HttpResponseOptions,
		queryParams?: QueryParameter[]
	): Promise<void> {
		const url = buildURL(this.objectType, ['Create'], queryParams);
		const response = await standardPost(url, item, { [scopedClientKey]: clientKey });
		await handleResponse(response, 'save', this.objectType, options);

		await this.invalidateKeys();
	}

	getAllForClientByIdsQuery(
		Ids: string[],
		clientSpaceId: string,
		queryParams?: QueryParameter[]
	): UseQueryOptions<ProjectSpaceConfig[], Error, ProjectSpaceConfig[], readonly unknown[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getAllByIdsPath,
				...Ids,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<ProjectSpaceConfig[]> => {
				const url = buildURL(this.objectType, [getAllByIdsPath], queryParams);
				const res = await standardPost(url, Ids, { [scopedClientKey]: clientSpaceId });
				await validateOkResponse(res, 'get all by IDs', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	getProjectPageQuery(
		limit: number,
		offset: number,
		projectIDs: string[],
		clientSpaceId: string
	): UseQueryOptions<ItemPage<ProjectSpaceConfig>, Error, ItemPage<ProjectSpaceConfig>> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getPagePath,
				limit.toString(),
				offset.toString(),
				projectIDs.join('&'),
			],
			queryFn: async (): Promise<ItemPage<ProjectSpaceConfig>> => {
				const url = buildURL(ProjectSpaceConfig.name, [
					getPagePath,
					limit.toString(),
					offset.toString(),
				]);
				const res = await standardPost(url, projectIDs, {
					[scopedClientKey]: clientSpaceId,
				});
				await validateOkResponse(res, 'get project page', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}
}
